import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { ICON_URLS, alpha } from "./constants";
import { useContainerWidth } from "./hooks/useContainerWidth";
import { ChatBotPreview } from "./components/ChatBotPreview";
import { BubblePreviewSide } from "./components/BubblePreviewSide";
import { SearchBarPreviewSide } from "./components/SearchBarPreviewSide";

export default function WidgetPreview({
  appearance = "chat-bot",
  themeMode = "light",
  customColors = {},
  chatBotColors = {},
  searchIconLogoOption = "teal",
  searchBubbleBgColor = "#219297",
  searchInputPosition = "bottom",
  companyName = "Ansera AI",
  isWhitelabelled = false
}) {
  const [open, setOpen] = useState(false);
  const dialogContentRef = useRef(null);
  const dialogWidth = useContainerWidth(dialogContentRef); 
  const isDark = themeMode === "dark";
  const isCustom = themeMode === "custom";
  console.log(searchIconLogoOption)
  const brand = chatBotColors.bubbleBackground || "#219297";
  const bubbleColor = searchBubbleBgColor || brand;
  
  let colors;
  if (isCustom) {
    colors = {
      bg: customColors.bg || "#ffffff",
      chatbox: customColors.answerbgcolor || "#fdfdfd",
      textPrimary: customColors.text || "#1a202c",
      textSec: customColors.text ? alpha(customColors.text, 0.55) : "#718096",
      border: customColors.inputBorder || "#e2e8f0",
      inputBg: customColors.bg || "#f7fafc",
      brand,
      buttonBg: customColors.buttonBg || "#000000",
      headerBg: customColors.chatheaderBg || "#ffffff",
      headerText: customColors.chatheadertextColor || "#1a202c",
      footerBg: customColors.chatfooterBg || "#f1f1f1",
      footerText: customColors.chatfootertextColor || "#1a202c",
      searchbarBg:"#ffffff",
      tilesBg: customColors.tilesbg || "#f9f9f9"
    };
  } else if (isDark) {
    colors = {
      bg: "#13151a",
      chatbox: "#1e2028",
      textPrimary: "#e8eaf0",
      textSec: "#9aa0b0",
      border: "rgb(187, 187, 187)",
      inputBg: "#252830",
      brand,
      headerBg: "#1e2028",
      headerText: "#e8eaf0",
      footerBg: "#13151a",
      footerText: "#9aa0b0",
      buttonBg: '#334155',
      searchbarBg:"#242424",
      tilesBg: "#3e3e42"
    };
  } else {
    colors = {
      bg: "#ffffff",
      chatbox: "#fdfdfd",
      textPrimary: "#1a202c",
      textSec: "#718096",
      border: "#e4e4e4",
      inputBg: "#f7fafc",
      brand,
      headerBg: "#ffffff",
      headerText: "#1a202c",
      footerBg: "#f9f9f9",
      footerText: "#718096",
      buttonBg: "rgb(51, 51, 51)",
      searchbarBg:"#ffffff",
      tilesBg: "#ffffff"
    };
  }

  const stackVertically = dialogWidth < 600;

  const getWidgetContent = () => {
    // SEARCH ICON TYPE - Show only chatbot preview
    if (appearance === "search-icon") {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            minHeight: 0,
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: stackVertically ? "100%" : "450px",
              margin: "0 auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                fontSize: "12px",
                fontWeight: 600,
                marginBottom: "8px",
                color: "#666",
                textAlign: "center",
              }}
            >
              Widget Preview
            </div>
            <div
              style={{
                borderRadius: 14,
                overflow: "hidden",
                border: `1px solid ${colors.border}`,
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 8px 32px rgba(0,0,0,0.16)",
                background: colors.bg,
                fontFamily: "system-ui, sans-serif",
                height: "min(500px, 100%)",
                minHeight: 0,
              }}
            >
              <ChatBotPreview
                colors={colors}
                company={companyName}
                inputPos={searchInputPosition}
              />
            </div>
          </div>
        </div>
      );
    }
    
    if (appearance === "chat-bot") {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: stackVertically ? "column" : "row",
            gap: 16,
            width: "100%",
            height: "100%",
            minHeight: 0,
          }}
        >
          <div
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: stackVertically ? "300px" : 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                fontSize: "12px",
                fontWeight: 600,
                marginBottom: "8px",
                color: "#666",
                textAlign: "center",
              }}
            >
              Live Website Preview (Floating Bubble)
            </div>
            <div style={{ flex: 1, minHeight: 0 }}>
              <BubblePreviewSide
                colors={colors}
                iconUrl={searchIconLogoOption}
                bubbleColor={bubbleColor}
                companyName={companyName}
                chatHeaderText={customColors.chat_widget_header_text || companyName}
                isWhitelabelled={isWhitelabelled}
              />
            </div>
          </div>

          <div
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: stackVertically ? "300px" : 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                fontSize: "12px",
                fontWeight: 600,
                marginBottom: "8px",
                color: "#666",
                textAlign: "center",
              }}
            >
              Widget Preview
            </div>
            <div
              style={{
                flex: 1,
                borderRadius: 14,
                overflow: "hidden",
                border: `1px solid ${colors.border}`,
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 8px 32px rgba(0,0,0,0.16)",
                background: colors.bg,
                fontFamily: "system-ui, sans-serif",
                minHeight: 0,
              }}
            >
              <ChatBotPreview
                colors={colors}
                company={companyName}
                inputPos={searchInputPosition}
              />
            </div>
          </div>
        </div>
      );
    }
    else if (appearance === "type-search") {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: stackVertically ? "column" : "row",
            gap: 16,
            width: "100%",
            height: "100%",
            minHeight: 0,
          }}
        >
          <div style={{ flex: 1, minWidth: 0, minHeight: stackVertically ? "300px" : 0, display: "flex", flexDirection: "column" }}>
            <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "8px", color: "#666", textAlign: "center" }}>
              Live Website Preview (Search Bar)
            </div>
            <div style={{ flex: 1, minHeight: 0 }}>
              <SearchBarPreviewSide
                colors={colors}
                iconUrl={searchIconLogoOption}
                bubbleColor={bubbleColor}
                companyName={companyName}
                suggestions={["What services?", "Pricing", "Contact us"]}
              />
            </div>
          </div>

          <div style={{ flex: 1, minWidth: 0, minHeight: stackVertically ? "300px" : 0, display: "flex", flexDirection: "column" }}>
            <div style={{ fontSize: "12px", fontWeight: 600, marginBottom: "8px", color: "#666", textAlign: "center" }}>
              Widget Preview
            </div>
            <div style={{ flex: 1, borderRadius: 14, overflow: "hidden", border: `1px solid ${colors.border}`, display: "flex", flexDirection: "column", boxShadow: "0 8px 32px rgba(0,0,0,0.16)", background: colors.bg, minHeight: 0 }}>
              <ChatBotPreview colors={colors} company={companyName} inputPos={searchInputPosition} />
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <Tooltip title="Preview widget" placement="top">
        <Button
          variant="outlined"
          startIcon={<RemoveRedEyeIcon />}
          onClick={() => setOpen(true)}
          sx={{
            borderRadius: "8px",
            px: { xs: 2, sm: 3 },
            py: 1.5,
            fontWeight: 600,
            textTransform: "none",
            borderColor: "#219297",
            color: "#219297",
            "&:hover": {
              borderColor: "#1a767a",
              backgroundColor: "rgba(33,146,151,0.06)",
            },
          }}
        >
          Preview
        </Button>
      </Tooltip>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth={false}
        fullScreen={false}
        PaperProps={{
          sx: {
            borderRadius: { xs: "12px", sm: "20px" },
            background: "transparent",
            boxShadow: "none",
            overflow: "visible",
            width: appearance === "search-icon" ? "min(95vw, 550px)" : "min(95vw, 900px)",
            maxWidth: "none",
            height: "min(92vh, 900px)",
            maxHeight: "none",
            m: { xs: 1, sm: 2 },
          },
        }}
        BackdropProps={{
          sx: {
            backdropFilter: "blur(6px)",
            backgroundColor: "rgba(0,0,0,0.45)",
          },
        }}
      >
        <DialogContent
          ref={dialogContentRef}
          sx={{
            p: { xs: 1.5, sm: 2 },
            overflow: "visible",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: 1, sm: 2 },
            height: "100%",
            boxSizing: "border-box",
          }}
        >
          <IconButton
            onClick={() => setOpen(false)}
            sx={{
              alignSelf: "flex-end",
              color: "#fff",
              background: "rgba(255,255,255,0.15)",
              "&:hover": { background: "rgba(255,255,255,0.25)" },
              flexShrink: 0,
            }}
            aria-label="Close preview"
          >
            <CloseIcon />
          </IconButton>

          <div
            style={{
              width: "100%",
              flex: 1,
              minHeight: 0,
              overflow: appearance === "chat-bot" ? "hidden" : "auto",
            }}
          >
            {getWidgetContent()}
          </div>

          <div
            style={{
              marginTop: 8,
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {[
              { label: appearance.replace("-", " "), color: "#219297" },
              { label: themeMode, color: "#718096" },
            ].map(({ label, color }) => (
              <span
                key={label}
                style={{
                  fontSize: 11,
                  padding: "3px 10px",
                  borderRadius: 20,
                  background: "rgba(255,255,255,0.12)",
                  color: "#fff",
                  border: `1px solid ${alpha("#ffffff", 0.2)}`,
                  textTransform: "capitalize",
                }}
              >
                {label}
              </span>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}